import api from "@/api/axios";
import router from "@/router";

const state = {
  token: localStorage.getItem("token") || null,
  user: null,
  authChecked: false,
  isAdmin: false,
  isStaff: false,
  isSuperAdmin: false,
  hasGrantedAccess: false,
};

const getters = {
  isAuthenticated: (state) => !!state.token,
  authChecked: (state) => state.authChecked,
  isAdmin: (state) => state.isAdmin,
  isStaff: (state) => state.isStaff,
  isSuperAdmin: (state) => state.isSuperAdmin,
  hasGrantedAccess: (state) => state.hasGrantedAccess,
  isBrandAdmin: (state, getters, rootState, rootGetters) => {
    const brandRole = rootGetters["brand/brandRole"];
    return (
      brandRole === "admin" ||
      brandRole === "owner" ||
      brandRole === "superuser"
    );
  },
  canAccessAdminPage:
    (state, getters, rootState, rootGetters) => (pageName) => {
      if (state.isSuperAdmin) return true;
      const brandRole = rootGetters["brand/brandRole"];
      if (brandRole !== "admin" && brandRole !== "owner") return false;
      const allowedPages = rootGetters["brand/allowedAdminPages"];
      return (allowedPages || []).includes(pageName);
    },
};

const actions = {
  async login({ commit, dispatch }, credentials) {
    try {
      // Clear any existing sessionid cookie before login to ensure clean state
      // This prevents old impersonation sessions from interfering
      document.cookie = "sessionid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "sessionid=; Max-Age=0; path=/;";
      
      const response = await api.post("/api/auth/login/", credentials);
      if (response.data.require_2fa) {
        return response.data;
      } else {
        const token = response.data.token;
        localStorage.setItem("token", token);
        commit("setToken", token);

        // Fetch user data immediately after login
        await dispatch("fetchUserData");

        return { require_2fa: false };
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  },
  async verifyTOTP({ commit }, { tempToken, totpCode }) {
    try {
      const response = await api.post("/api/totp/", {
        temp_token: tempToken,
        token: totpCode,
      });
      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem("token", token);
        commit("setToken", token);
        commit("setUser", response.data);
        return { success: true };
      } else {
        throw new Error(response.data.error || "Verification failed");
      }
    } catch (error) {
      console.error("TOTP verification failed:", error);
      throw error;
    }
  },
  async logout({ commit, dispatch }) {
    // Immediately set isAuthenticated to false to hide the navbar
    commit("setToken", null);
    commit("setUser", null);
    commit("setAuthChecked", true);
    commit("setIsAdmin", false);
    commit("setIsStaff", false);
    commit("setIsSuperAdmin", false);
    commit("setHasGrantedAccess", false);
    commit("brand/clearBrand", null, { root: true });

    // Then perform cleanup operations
    const currentTheme = localStorage.getItem("theme") || "dark";
    
    // Clear all auth-related data
    localStorage.removeItem("token");
    localStorage.removeItem("accountId");
    localStorage.removeItem("portalToken");
    
    // Clear session storage
    sessionStorage.clear();
    
    // Clear cookies that might be from impersonation sessions
    // ESPECIALLY the sessionid cookie which causes the issue
    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      // Specifically target sessionid and other session cookies, but keep CSRF token
      if (name && name !== 'csrftoken') {
        // Try multiple domain variations to ensure cookie is deleted
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.localhost`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
        // Also try without domain to match how it was set
        document.cookie = `${name}=; Max-Age=0; path=/;`;
        document.cookie = `${name}=; Max-Age=-99999999; path=/;`;
      }
    });

    // Preserve the current theme
    await dispatch("theme/setThemeWithoutAPI", currentTheme, { root: true });

    // Use router.push with replace: true instead of router.replace
    router.push({ path: "/login" }, () => {}, { replace: true });
  },
  async checkAuth({ commit, dispatch, state }) {
    if (state.authChecked) return state.token !== null;

    const token = localStorage.getItem("token");

    if (!token) {
      commit("setAuthChecked", true);
      return false;
    }

    try {
      const response = await api.get("/api/auth/user/");
      commit("setToken", token);
      commit("setUser", response.data);
      commit("setAuthChecked", true);
      commit("setIsStaff", response.data.is_staff);
      commit("setIsSuperAdmin", response.data.is_admin);
      commit("setIsAdmin", response.data.is_staff); // Keep for backward compatibility
      commit("setHasGrantedAccess", response.data.has_granted_access || false);

      // Also update the profile store with the user data
      commit("profile/setProfile", response.data, { root: true });

      // Fetch brand data for role-aware navigation
      await dispatch("brand/fetchBrandCurrent", null, { root: true });

      return true;
    } catch (error) {
      console.error("Token validation failed:", error);
      if (error.response && error.response.status === 500) {
        console.error("Server error occurred. Please try again later.");
      }
      commit("setToken", null);
      commit("setUser", null);
      commit("setIsAdmin", false);
      commit("setIsStaff", false);
      commit("setIsSuperAdmin", false);
      commit("setHasGrantedAccess", false);
      commit("brand/clearBrand", null, { root: true });
      localStorage.removeItem("token");
      commit("setAuthChecked", true);
      return false;
    }
  },
  async updateToken({ commit }, token) {
    localStorage.setItem("token", token);
    commit("setToken", token);
  },
  setToken({ commit }, token) {
    commit("setToken", token);
    localStorage.setItem("token", token);
  },
  setUser({ commit }, user) {
    commit("setUser", user);
  },
  async fetchUserData({ commit, dispatch }) {
    try {
      const response = await api.get("/api/auth/user/");
      commit("setUser", response.data);
      commit("setIsStaff", response.data.is_staff);
      commit("setIsSuperAdmin", response.data.is_admin);
      commit("setIsAdmin", response.data.is_staff); // Keep for backward compatibility
      commit("setHasGrantedAccess", response.data.has_granted_access || false);
      // Update theme based on user preference
      dispatch("theme/setTheme", response.data.color_mode || "dark", {
        root: true,
      });

      // Fetch brand data for role-aware navigation
      await dispatch("brand/fetchBrandCurrent", null, { root: true });

      return response.data;
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      throw error;
    }
  },
};

const mutations = {
  setToken(state, token) {
    state.token = token;
    localStorage.setItem("token", token);
  },
  setUser(state, user) {
    state.user = user;
    state.isAdmin = user ? user.is_staff : false;
  },
  setAuthChecked(state, checked) {
    state.authChecked = checked;
  },
  setIsAdmin(state, isAdmin) {
    state.isAdmin = isAdmin;
  },
  setIsStaff(state, isStaff) {
    state.isStaff = isStaff;
  },
  setIsSuperAdmin(state, isSuperAdmin) {
    state.isSuperAdmin = isSuperAdmin;
  },
  setHasGrantedAccess(state, hasGrantedAccess) {
    state.hasGrantedAccess = hasGrantedAccess;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
